// The rating picker, which is two steps deep and never looks it.
//
//   step 1   the five ratings, each hinted with where it peaks for the place on
//            screen; then "My kind of day ›" and "A single measurement ›"
//   step 2   whichever of those two was chosen, in the SAME surface, opening
//            with "‹ All ratings"
//
// Two steps rather than one long list because the list would otherwise mix
// three different kinds of thing — five judgements, one authoring tool and seven
// raw measurements — and a reader choosing between "Sunbathing" and "Humidity"
// is being asked a question that does not mean anything. Depth here is what
// keeps the resting state to five rows.
//
// The peak hints are the reason this surface is worth opening: "Jul · 97" beside
// Sunbathing answers "is this place any good for that, and when" before the
// reader has chosen anything. They come from the open city's series — the same
// one the ruler sizes its ticks from — and are simply ABSENT when no city is
// open. A dash or a zero there would be an invention.

import { $ } from '../dom.ts';
import { state, store } from '../state.ts';
import { activityKeys } from './chrome.ts';
import { menuRow } from './sentence.ts';
import { VAR, VARIANT_GROUPS } from '../../lib/variables.ts';
import { bestMonthFor } from '../../lib/city-stats.ts';
import type { VarKey, VariantGroup } from '../../lib/variables.ts';

export interface LensMenuDeps {
  applyVariable: (v: VarKey) => void;
  /** Reveal the custom rating and open its editor — step 2's whole content, for now. */
  openRecipeEditor: () => void;
}

let deps: LensMenuDeps;

export function initLensMenu(d: LensMenuDeps): void {
  deps = d;
}

// "Jul · 97" for the place on screen, or '' when there is no place on screen.
function peakHint(v: VarKey): string {
  const s = state.series;
  if (!s || !s.series || !s.series[v]) return '';
  const best = bestMonthFor(s, v, state.dates);
  return best ? `${best.month.slice(0, 3)} · ${best.score}` : '';
}

function hairline(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'menu-rule';
  return el;
}

function caveat(text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'menu-caveat';
  el.textContent = text;
  return el;
}

// The way back, and the first thing in either step 2.
function backRow(): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'menu-back';
  b.textContent = '‹ All ratings';
  b.addEventListener('click', () => store.set({ menu: 'lens' }));
  return b;
}

// ── step 1 ──────────────────────────────────────────────────────────────────
function renderRoot(box: HTMLElement): void {
  const list = document.createElement('div');
  list.className = 'menu-list';

  // The curated five. `custom` is filtered out rather than taken as it comes:
  // activityKeys() starts including it the moment it is unlocked, and it has its
  // own row below with its own behaviour — without this the picker grows a
  // second "My kind of day" that only switches lens, and being first, it is the
  // one a reader hits.
  for (const v of activityKeys()) {
    if (VAR[v].custom) continue;
    const row = menuRow(VAR[v].label, peakHint(v), { active: state.variable === v });
    if (VAR[v].help) row.title = VAR[v].help;
    row.addEventListener('click', () => { deps.applyVariable(v); store.set({ menu: null }); });
    list.appendChild(row);
  }

  // Not a special green call to action — the same row shape as the five above,
  // because it is the same KIND of choice: which rating is showing. The '›' is
  // the difference: this one has a second step behind it.
  const mine = menuRow(
    VAR.custom.label,
    state.customOn ? peakHint('custom') : 'not set up yet',
    { active: state.variable === 'custom', trailing: '›' },
  );
  mine.addEventListener('click', () => store.set({ menu: 'lens-custom' }));
  list.appendChild(mine);

  const raw = menuRow('A single measurement', 'temperature, cloud, rain…', { trailing: '›' });
  raw.addEventListener('click', () => store.set({ menu: 'lens-raw' }));

  box.replaceChildren(
    list,
    hairline(),
    raw,
    caveat('Each is rated Poor / Fair / Good / Ideal for a typical such day, averaged across the years. They rate the weather, not the place — “Sunbathing” asks whether it is warm and sunny, not whether there is a beach.'),
  );
}

// ── step 2a: the raw layers ─────────────────────────────────────────────────
// Exactly VARIANT_GROUPS, which is already the "two decisions" list the header
// dropdown was built from — no new data, and the units become the hint.
function renderRaw(box: HTMLElement): void {
  const list = document.createElement('div');
  list.className = 'menu-list';
  for (const g of Object.values(VARIANT_GROUPS) as VariantGroup[]) {
    for (const [k, label] of g.members) {
      const row = menuRow(label, VAR[k].unit || '', { active: state.variable === k });
      row.addEventListener('click', () => { deps.applyVariable(k); store.set({ menu: null }); });
      list.appendChild(row);
    }
  }
  box.replaceChildren(
    backRow(),
    list,
    caveat('A measurement is not a rating: the ruler’s ticks go flat while one is showing.'),
  );
}

// ── step 2b: my kind of day ─────────────────────────────────────────────────
// The step contributes only the way back; the editor below it is markup that is
// already there and already wired, revealed by renderLensMenu.
function renderCustom(steps: HTMLElement): void {
  steps.replaceChildren(backRow());
  deps.openRecipeEditor(); // unlock the rating, select it, reveal and sync the fields
}

// The one entry point: state.menu says which step, and this draws it.
//
// The rows go into #lens-steps; the editor is a permanent sibling that only
// gets shown or hidden. Rebuilding the rows must never touch it — it holds the
// listeners wireCustomEditor attached at boot, and a replaceChildren that swept
// it up would take them with it.
export function renderLensMenu(): void {
  const m = state.menu;
  if (!m || !String(m).startsWith('lens')) return;
  const steps = $('lens-steps');
  $('custom-editor').hidden = m !== 'lens-custom';
  if (m === 'lens-raw') renderRaw(steps);
  else if (m === 'lens-custom') renderCustom(steps);
  else renderRoot(steps);
}
